import React, { useState } from "react";
import Slider from "react-slick";
import { motion } from "framer-motion";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Play, Info } from "lucide-react";
import { Link } from "react-router-dom";

const SeriesSlider = ({ series }) => {
  const [activeSeries, setActiveSeries] = useState(series[0]);

  const settings = {
    dots: false,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: true,
    fade: true,
    beforeChange: (_, newIndex) => {
      setActiveSeries(series[newIndex]);
    },
  };

  const fallbackBackground = "/images/background1.png";

  const backgroundImage = activeSeries.backdrop_path
    ? `url(https://image.tmdb.org/t/p/original${activeSeries.backdrop_path})`
    : activeSeries.banner
      ? `url(${activeSeries.banner.replace(/\s+/g, "%20")})`
      : `url(${fallbackBackground})`;

  return (
    <div className="relative w-full overflow-hidden min-h-[90vh]">
      {/* Background Image with Subtle Overlay */}
      <div
        key={activeSeries.id}
        className="absolute top-0 left-0 w-full h-full bg-cover bg-center"
        style={{
          backgroundImage,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/50 to-transparent"></div>
        <div className="absolute inset-0 bg-black/40"></div>
      </div>

      {/* Slider Content */}
      <div className="relative z-10 container mx-auto px-4 h-full flex items-center pt-20 pb-10">
        <Slider {...settings} className="w-full">
          {series.map((serie) => (
            <div
              key={serie.id}
              className="flex flex-col lg:flex-row items-center gap-8 py-10"
            >
              {/* Series Poster */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative group"
              >
                <img
                  src={
                    serie.poster_path
                      ? `https://image.tmdb.org/t/p/w500${serie.poster_path}`
                      : serie.poster
                  }
                  alt={serie.title || serie.name}
                  className="w-[250px] md:w-[300px] lg:w-[350px] h-auto rounded-lg shadow-lg transition-all duration-300 group-hover:shadow-xl"
                />
              </motion.div>

              {/* Series Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex flex-col gap-4 text-white max-w-lg"
              >
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-shadow">
                  {serie.title || serie.name}
                </h1>

                <div className="flex flex-wrap items-center gap-3 text-sm md:text-base">
                  {serie.vote_average && (
                    <span className="font-semibold text-green-400">
                      {Math.round(serie.vote_average * 10)}% coincidencia
                    </span>
                  )}
                  {serie.first_air_date && (
                    <span>{serie.first_air_date.split("-")[0]}</span>
                  )}
                  <span className="px-2 py-0.5 border border-gray-300 rounded-md">16+</span>
                  {serie.seasons_count && <span>{serie.seasons_count} temporadas</span>}
                  <span className="px-2 py-0.5 border border-gray-300/40 rounded-md text-xs">
                    HD
                  </span>
                </div>
                <p className="text-gray-100 leading-relaxed">
                  {serie.overview || serie.description}
                </p>

                {/* Series Actions */}
                <div className="flex items-center gap-3 mt-6">
                  <Link to={`/series/${serie.id}`}>
                    <button className="flex items-center px-6 py-3 text-sm font-medium text-black bg-white rounded-md hover:bg-white/90 transition-colors">
                      <Info className="w-5 h-5 mr-2" />
                      Más información
                    </button>
                  </Link>
                </div>
              </motion.div>
            </div>
          ))}
        </Slider>
      </div>
    </div>
  );
};

export default SeriesSlider;
